import { useRef, useState, type FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReactToPrint } from 'react-to-print';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// common
import Icon from 'common/components/Icon';
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import SchemaForm from 'common/SchemaForm';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';

// screen
import { type ClientModel } from 'modules/map/types';
import {
  NutritionMenuFilterModel,
  NutritionMenuStatusEnum,
} from 'modules/map/components/nutrition-menu/types';
import { A4_PRINT_SIZE, formatUTC } from 'modules/map/components/pdf-print-form/utils';
import TeacherMenuTable from 'modules/map/components/teacher-menu/TeacherMenuTable';
import { TeacherMenuModel } from 'modules/map/components/teacher-menu/types';
import PdfTeacherMenu from 'modules/map/components/teacher-menu/pdf';

interface Props {
  clientData: ClientModel;
  open: boolean;
  onClose: () => void;
}

const TeacherMenu: FC<Props> = (props) => {
  const { open, onClose, clientData } = props;

  const intl = useIntl();
  const contentToPrint = useRef(null);
  const service = useService();

  // Tháng/tuần do khung thực đơn của năm học quyết định — hook tự chọn khung mới nhất khi tải xong.
  const [filter, setFilter] = useState<NutritionMenuFilterModel>({
    state: NutritionMenuStatusEnum.propose,
  });
  const { monthOptions, weekOptions, framesFailed } = useSchoolYearMonthFilter(filter, setFilter);

  // Năm lấy từ tháng đã chọn, không phải dayjs().year(): học kỳ 2 nằm ở năm dương lịch sau.
  // Thiếu tuần thì KHÔNG dựng searchStr: bỏ trống một đoạn sẽ làm `day` trượt vào chỗ của `week`.
  const searchStr = filter.week
    ? buildResourceSearchStr({
        monthKey: filter.month,
        week: filter.week,
        day: 0,
        state: filter.state,
      })
    : null;

  const orgId = clientData.operationCode;

  const { data, isFetching } = useQuery({
    queryKey: [filter, orgId, 'teacher-nutrition-menu'],
    placeholderData: undefined,
    queryFn: async () => {
      if (!service || !searchStr || !orgId) return null;
      const json = await service.ClientTeacherMenuController_list({
        params: {
          orgId,
          mode: 'real',
        },
        queries: {
          limit: 50,
          offset: 0,
          searchStr,
        },
      });
      return (json?.[0] as unknown as TeacherMenuModel) ?? null;
    },
    enabled: open && !!service && !!searchStr && !!orgId,
  });

  const days = data?.structuredData.data.items || [];
  const fromDate = formatUTC(days[0]?.datetime || '', 'L');
  const lastItemIndex = days.length - 1;
  const toDate = formatUTC(days[lastItemIndex]?.datetime || '', 'L');

  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    pageStyle: A4_PRINT_SIZE,
  });

  const renderTable = ({
    isPrintedTable = false,
    refProp = null,
  }: {
    isPrintedTable?: boolean;
    refProp?: React.Ref<HTMLDivElement>;
  }) => {
    const renderTableContent = () => {
      if (isFetching) return <MuiLoading />;
      if (!data?.structuredData?.metadata?.isUsed) return <NotFoundBox />;

      if (!isPrintedTable) {
        return <TeacherMenuTable data={data} filter={filter} />;
      }
      return <PdfTeacherMenu data={data} />;
    };

    return (
      <Box
        sx={{
          overflow: isPrintedTable ? 'hidden' : undefined,
          height: isPrintedTable ? 0 : '100%',
        }}
      >
        <Box ref={refProp} className="flex justify-center w-full h-full">
          {renderTableContent()}
        </Box>
      </Box>
    );
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title={'map.teacherNutritionMenu'}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      disablePortal
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        },
      }}
    >
      <Box className="p-2 !pb-0 tablet:p-4">
        <SchemaForm<NutritionMenuFilterModel>
          formData={filter}
          onChange={setFilter}
          globalProps={{ noHelperText: true }}
          schema={{
            fields: ({ formProps: { isMobile } }) => {
              return {
                blank2: {
                  mode: 'node',
                  hidden: !isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography variant="subtitle1">
                          {[fromDate, toDate].join(' - ')}
                        </Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: 12 },
                },
                monthText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.month" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                month: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.month" />,
                  options: monthOptions,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
                weekText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box>
                        <Typography mt={1}>
                          <FormattedMessage id="map.week" />
                        </Typography>
                        {weekOptions.length === 0 && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {intl.formatMessage({
                              id: framesFailed ? 'map.weekLoadFailed' : 'map.noWeek',
                            })}
                          </Typography>
                        )}
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                // Tuần phụ thuộc tháng nên đứng SAU tháng và khoá lại khi tháng chưa chốt khung.
                week: {
                  mode: 'select',
                  label: isMobile && <FormattedMessage id="map.week" />,
                  options: weekOptions.map((w) => ({ id: w.id, name: w.id })),
                  disabled: weekOptions.length === 0,
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: { mobile: 6, tablet: 2 },
                },

                blank: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Box className="flex items-center justify-center h-full">
                        <Typography>{data && [fromDate, toDate].join(' - ')}</Typography>
                      </Box>
                    );
                  },
                  propsWrapper: { mobile: true },
                },
                statusText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.process" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                state: {
                  label: isMobile && <FormattedMessage id="map.process" />,
                  mode: 'select',
                  options: [
                    {
                      id: NutritionMenuStatusEnum.propose,
                      name: intl.formatMessage({ id: 'map.propose' }),
                    },
                    {
                      id: NutritionMenuStatusEnum.confirm,
                      name: intl.formatMessage({ id: 'confirm' }),
                    },
                    {
                      id: NutritionMenuStatusEnum.complete,
                      name: intl.formatMessage({ id: 'map.completed' }),
                    },
                  ],
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
              };
            },
          }}
        />
      </Box>
      <Box
        sx={{
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderTable({ isPrintedTable: true, refProp: contentToPrint })}
        {renderTable({})}
      </Box>
      <Box className="p-2 tablet:p-4 flex justify-end gap-2 tablet:gap-4">
        <Button
          startIcon={<Icon icon={Download} size={16} />}
          onClick={() => {
            handlePrint();
          }}
          disabled={!data}
        >
          <FormattedMessage id="map.download" />
        </Button>
      </Box>
    </MuiDialog>
  );
};

export default TeacherMenu;
